import {
  TerraUIMessageDataPartName,
  type TerraUIMessage,
  type TerraUIMessagePart,
} from './messages.types';

export const HISTORY_PLACEMENT_METADATA_KEY = 'historyPlacement';

export type HistoryPlacement = {
  partKeysByMessageId: Readonly<Record<string, readonly string[]>>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toolCallIdFromData(data: unknown): string | undefined {
  if (!isRecord(data)) {
    return undefined;
  }

  const toolCallId = data.toolCallId;

  return typeof toolCallId === 'string' && toolCallId.length > 0
    ? toolCallId
    : undefined;
}

export function chatMessagePartKey(part: TerraUIMessagePart): string {
  if (part.type === TerraUIMessageDataPartName) {
    const toolCallId = toolCallIdFromData(part.data);

    if (toolCallId) {
      return `tool:${toolCallId}`;
    }

    if (typeof part.id === 'string' && part.id.length > 0) {
      return `id:${part.id}`;
    }
  }

  if (part.type === 'text') {
    return `text:${part.text}`;
  }

  if (part.type === 'reasoning') {
    return `reasoning:${part.text}`;
  }

  return `raw:${part.type}`;
}

export function messagePartKeys(message: TerraUIMessage): readonly string[] {
  return (message.parts ?? []).map((part) => chatMessagePartKey(part));
}

export function readHistoryPlacement(
  metadata: unknown,
): HistoryPlacement | null {
  if (!isRecord(metadata)) {
    return null;
  }

  const placement = metadata[HISTORY_PLACEMENT_METADATA_KEY];

  if (!isRecord(placement) || !isRecord(placement.partKeysByMessageId)) {
    return null;
  }

  const partKeysByMessageId: Record<string, readonly string[]> = {};

  for (const [messageId, keys] of Object.entries(
    placement.partKeysByMessageId,
  )) {
    if (!Array.isArray(keys) || keys.some((key) => typeof key !== 'string')) {
      continue;
    }

    partKeysByMessageId[messageId] = keys;
  }

  if (Object.keys(partKeysByMessageId).length === 0) {
    return null;
  }

  return { partKeysByMessageId };
}

const AFTER_USER_MESSAGE_PREFIX = 'after-user:';

export type OrderedHistoryMessage = {
  order: number;
  message: TerraUIMessage;
};

export function projectOrderedHistoryPage({
  page,
  related,
}: {
  page: readonly OrderedHistoryMessage[];
  related: readonly OrderedHistoryMessage[];
}): TerraUIMessage[] {
  const pageIds = new Set(page.map((item) => item.message.id));
  const projected = projectHistoryPlacements(
    [...page, ...related.filter((item) => !pageIds.has(item.message.id))]
      .sort((left, right) => left.order - right.order)
      .map((item) => item.message),
  );

  return projected.filter((message) => {
    if (message.id.startsWith(AFTER_USER_MESSAGE_PREFIX)) {
      return pageIds.has(message.id.slice(AFTER_USER_MESSAGE_PREFIX.length));
    }

    return pageIds.has(message.id);
  });
}

export function projectHistoryPlacements(
  messages: readonly TerraUIMessage[],
): TerraUIMessage[] {
  const anchors = messages.flatMap((message) => {
    if (message.role !== 'user') {
      return [];
    }

    const placement = readHistoryPlacement(message.metadata);

    if (!placement) {
      return [];
    }

    return Object.entries(placement.partKeysByMessageId).map(
      ([assistantMessageId, snapshot]) => ({
        userMessageId: message.id,
        assistantMessageId,
        sentAfterPartCount: snapshot.length,
      }),
    );
  });

  if (anchors.length === 0) {
    return [...messages];
  }

  const overflowByUserId = new Map<string, TerraUIMessagePart[]>();
  const emptiedIds = new Set<string>();
  const rewritten = messages.map((message) => {
    const cuts = anchors
      .filter((anchor) => anchor.assistantMessageId === message.id)
      .sort(
        (left, right) => left.sentAfterPartCount - right.sentAfterPartCount,
      );

    if (cuts.length === 0) {
      return message;
    }

    const kept: TerraUIMessagePart[] = [];
    const parts = message.parts ?? [];

    parts.forEach((part, index) => {
      let destination: string | undefined;

      for (const cut of cuts) {
        if (index >= cut.sentAfterPartCount) {
          destination = cut.userMessageId;
        }
      }

      if (destination === undefined) {
        kept.push(part);
        return;
      }

      overflowByUserId.set(destination, [
        ...(overflowByUserId.get(destination) ?? []),
        part,
      ]);
    });

    if (kept.length === parts.length) {
      return message;
    }

    if (kept.length === 0) {
      emptiedIds.add(message.id);
    }

    return { ...message, parts: kept };
  });

  if (overflowByUserId.size === 0) {
    return rewritten;
  }

  const result: TerraUIMessage[] = [];

  for (const message of rewritten) {
    if (emptiedIds.has(message.id)) {
      continue;
    }

    result.push(message);
    const overflow = overflowByUserId.get(message.id);

    if (!overflow?.length) {
      continue;
    }

    const laterKeys = new Set(
      rewritten
        .slice(rewritten.indexOf(message) + 1)
        .flatMap((item) => messagePartKeys(item)),
    );
    const unseen = overflow.filter(
      (part) => !laterKeys.has(chatMessagePartKey(part)),
    );

    if (unseen.length === 0) {
      continue;
    }

    result.push({
      id: `${AFTER_USER_MESSAGE_PREFIX}${message.id}`,
      role: 'assistant',
      parts: unseen,
    });
  }

  return result;
}
